import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import useWorkOrderService from '../services/WorkOrderService/workOrder.service';
import { DEFAULT_WORK_ORDER_SETTINGS } from '../views/InfrastructureManager/ConfigurationSettings/constants';
import { STATUS_ORDER } from '../views/InfrastructureManager/WorkOrders/constants';
import type { WorkOrderStatus } from '../enums/workOrderStatus';
import type { WorkOrderSettings } from '../types/InfrastructureManagerTypes';

// The signed-in user's work-order settings (read-only), shared by the settings page, the board and the calendar.
export const useWorkOrderSettings = () => {
  const { getWorkOrderSettings } = useWorkOrderService();
  const { data, isLoading } = useQuery(getWorkOrderSettings());

  // A status the API leaves out falls back to its default, so no consumer reads an undefined entry.
  const settings = useMemo<WorkOrderSettings>(() => {
    if (!data) return DEFAULT_WORK_ORDER_SETTINGS;
    const statuses = Object.fromEntries(
      STATUS_ORDER.map((status) => [status, { ...DEFAULT_WORK_ORDER_SETTINGS.statuses[status], ...data.statuses?.[status] }]),
    ) as Record<WorkOrderStatus, WorkOrderSettings['statuses'][WorkOrderStatus]>;
    return { zoomToLastHighlighted: data.zoomToLastHighlighted, statuses };
  }, [data]);
  // Stable while the settings are, so column layout only re-fits when visibility changes.
  const visibleStatuses = useMemo(() => STATUS_ORDER.filter((status) => settings.statuses[status].visible), [settings]);

  return { settings, visibleStatuses, isLoading };
};
